import { useEffect, useState } from 'react';
import { describeRosterAdoption, parseRosterEntry, type RosterEntryAdoption } from '@/lib/onboarding/rosterAdoption';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
// DTC-K: route the connect-offer `updated_at` lifecycle write through
// the canonical clock so the persisted state-change is deterministic
// under `clock.setFixed(...)`.
import { clock } from '@/utils/clock';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Gift, ChevronDown, ChevronUp, DollarSign, Clock, Heart, StickyNote, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { fetchCounterpartyShopNames } from '@/hooks/useConnectApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/** Presentation only — the label the shop's authorization decision reads as. */
const ROLE_LABELS: Record<string, string> = {
  TECHNICIAN: 'Technician',
  SERVICE_WRITER: 'Service Writer',
  MANAGER: 'Manager',
  DISPATCHER: 'Dispatcher',
  PARTS: 'Parts',
  AUDITOR: 'Auditor',
};

interface Offer {
  id: string;
  shop_id: string;
  /**
   * The role THE SHOP is offering. Read-only to the recipient — the database
   * refuses to let them change it — and shown here because accepting an offer
   * whose authority is invisible is not informed consent to it.
   */
  offered_role: string | null;
  /**
   * WS-GAPS2 B-E4: the pre-seeded roster entry the shop says this person already is (CORE-W:45). Accepting such an
   * offer takes that entry over; the dialog discloses it and the acceptance carries its id back.
   */
  technician_id?: string | null;
  pay_type: string | null;
  pay_rate: number | null;
  bonus_criteria: string | null;
  working_hours: any;
  health_insurance: boolean;
  dental_insurance: boolean;
  vision_insurance: boolean;
  life_insurance: boolean;
  retirement_401k: boolean;
  retirement_match: string | null;
  pto_days: number | null;
  uniforms_provided: boolean;
  tools_provided: boolean;
  additional_benefits: string | null;
  notes: string | null;
  status: string;
  decline_reason: string | null;
  created_at: string;
  shops?: { name: string } | null;
}

const DAY_LABELS: Record<string, string> = {
  Sunday: 'Sun', Monday: 'Mon', Tuesday: 'Tue', Wednesday: 'Wed',
  Thursday: 'Thu', Friday: 'Fri', Saturday: 'Sat',
};

/**
 * WS-GAPS2 B-E4 — what the acceptance dialog knows about the roster entry an offer names.
 *   none        the offer names no entry: accept exactly as before
 *   loading     asking the server what the entry is (accept is disabled meanwhile)
 *   ready       disclosed; accepting sends back exactly this entry's id
 *   unavailable the offer names an entry that can no longer be taken over: nothing can be accepted until the shop
 *               updates its offer (accepting would duplicate it — CORE-W:45)
 */
export type ConnectRosterDisclosure =
  | { state: 'none' }
  | { state: 'loading' }
  | { state: 'ready'; entry: RosterEntryAdoption }
  | { state: 'unavailable' };

type LooseRpc = { rpc: (f: string, a: Record<string, unknown>) => PromiseLike<{ data: unknown; error: unknown }> };

export async function loadConnectRosterDisclosure(offer: Pick<Offer, 'id' | 'technician_id'>): Promise<ConnectRosterDisclosure> {
  if (!offer.technician_id) return { state: 'none' };
  try {
    const { data, error } = await (supabase as unknown as LooseRpc).rpc('connect_offer_roster_entry_preview', { p_offer_id: offer.id });
    const entry = error ? null : parseRosterEntry(data);
    // The server's answer must be the entry the offer names; anything else is not something the person can accept.
    return entry && entry.technicianId === offer.technician_id ? { state: 'ready', entry } : { state: 'unavailable' };
  } catch {
    return { state: 'unavailable' };
  }
}

/** The accept request body: the disclosed entry id goes back ONLY when it was disclosed. */
export function acceptOfferBody(offerId: string, startDate: string, disclosure: ConnectRosterDisclosure): Record<string, string> {
  return disclosure.state === 'ready'
    ? { offer_id: offerId, start_date: startDate, adopt_roster_entry: disclosure.entry.technicianId }
    : { offer_id: offerId, start_date: startDate };
}

/** Convert "14:30" to "2:30 PM" */
function to12Hour(time: string): string {
  if (!time) return time;
  const [hStr, mStr] = time.split(':');
  let h = parseInt(hStr, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mStr} ${ampm}`;
}

export function OffersReceived() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [decliningId, setDecliningId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [rosterDisclosure, setRosterDisclosure] = useState<ConnectRosterDisclosure>({ state: 'none' });

  const { data: offers, isLoading } = useQuery({
    queryKey: ['connect-offers-received', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('connect_offers')
        .select('*')
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      // Core-100 direction 15: shop rows are not readable across tenants; the
      // offering shop's name comes from the Connect relationship itself.
      const offers = (data || []) as Offer[];
      const names = await fetchCounterpartyShopNames(offers.map((o) => o.shop_id));
      return offers.map((o) => ({ ...o, shops: names[o.shop_id] ? { name: names[o.shop_id] } : null }));
    },
    enabled: !!user?.id,
  });

  const respond = useMutation({
    mutationFn: async ({ offerId, action, reason, start_date, disclosure }: { offerId: string; action: 'accepted' | 'declined'; reason?: string; start_date?: string; disclosure?: ConnectRosterDisclosure }) => {
      if (action === 'accepted') {
        // Use edge function for acceptance - creates membership, technician, etc.
        const { data, error } = await supabase.functions.invoke('accept-connect-offer', {
          body: acceptOfferBody(offerId, start_date ?? '', disclosure ?? { state: 'none' }),
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        return data;
      } else {
        // Decline is a simple status update
        const update: any = { status: action, updated_at: clock.now().toISOString() };
        if (reason) update.decline_reason = reason;
        const { error } = await supabase.from('connect_offers').update(update).eq('id', offerId);
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['connect-offers-received'] });
      qc.invalidateQueries({ queryKey: ['connect-offers-sent'] });
      toast.success(vars.action === 'accepted' ? '🎉 Offer Accepted! You\'ve been added to the shop and your schedule is set.' : '❌ Offer Declined. The shop has been notified.');
      setDecliningId(null);
      setDeclineReason('');
      setAcceptingId(null);
      setStartDate('');
    },
    onError: (e: Error) => {
      toast.error('Failed to respond', { description: e.message });
    },
  });

  // WS-GAPS2 B-E4: when the dialog opens for an offer that names a roster entry, ask what it is — BEFORE the person
  // can accept (CORE-R:82: the acceptance is theirs and informed).
  const acceptingOffer = (offers ?? []).find((o) => o.id === acceptingId) ?? null;
  const acceptingTechnicianId = acceptingOffer?.technician_id ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!acceptingId || !acceptingTechnicianId) { setRosterDisclosure({ state: 'none' }); return; }
    setRosterDisclosure({ state: 'loading' });
    void loadConnectRosterDisclosure({ id: acceptingId, technician_id: acceptingTechnicianId })
      .then((d) => { if (!cancelled) setRosterDisclosure(d); });
    return () => { cancelled = true; };
  }, [acceptingId, acceptingTechnicianId]);
  const rosterBlocksAccept = rosterDisclosure.state === 'loading' || rosterDisclosure.state === 'unavailable';

  if (isLoading) return null;
  if (!offers?.length) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">Offers</CardTitle>
          </div>
          <CardDescription>No offers yet. When a shop sends you an offer, it will appear here.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Gift className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">Offers</CardTitle>
          <Badge variant="secondary">{offers.length}</Badge>
        </div>
        <CardDescription>Review offers from shops you've connected with.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {offers.map((offer) => {
          const expanded = expandedId === offer.id;
          const shopName = offer.shops?.name || 'Shop';
          const workingDays = Array.isArray(offer.working_hours)
            ? offer.working_hours.filter((d: any) => d.enabled)
            : [];
          const isPending = offer.status === 'sent' || offer.status === 'pending';
          const isDeclineFlow = decliningId === offer.id;

          return (
            <div key={offer.id} className="rounded-lg border">
              <button
                className="w-full flex items-center justify-between p-3 hover:bg-accent/50 transition-colors text-left"
                onClick={() => setExpandedId(expanded ? null : offer.id)}
              >
                <div>
                  <p className="text-sm font-medium">{shopName}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(offer.created_at).toLocaleDateString()}
                    {offer.offered_role && ` · ${ROLE_LABELS[offer.offered_role] || offer.offered_role}`}
                    {offer.pay_type && ` · ${offer.pay_type.replace('_', ' ')}`}
                    {offer.pay_rate != null && ` · $${offer.pay_rate.toLocaleString()}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={isPending || offer.status === 'accepting' ? 'secondary' : offer.status === 'accepted' ? 'default' : 'destructive'}
                    className="capitalize text-xs"
                  >
                    {offer.status}
                  </Badge>
                  {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                </div>
              </button>

              {expanded && (
                <div className="border-t px-3 pb-3 pt-2 space-y-4 text-sm">
                  {/* Role — the shop's decision, stated plainly before the money */}
                  {offer.offered_role && (
                    <div>
                      <div className="font-medium mb-1">Role</div>
                      <p className="text-muted-foreground pl-5">
                        <span className="text-foreground">
                          {ROLE_LABELS[offer.offered_role] || offer.offered_role}
                        </span>{' '}
                        at {shopName}
                      </p>
                    </div>
                  )}

                  {/* Pay */}
                  <div>
                    <div className="flex items-center gap-1.5 font-medium mb-1">
                      <DollarSign className="h-3.5 w-3.5" /> Pay Structure
                    </div>
                    <div className="text-muted-foreground space-y-0.5 pl-5">
                      {offer.pay_type && <p>Type: <span className="text-foreground capitalize">{offer.pay_type.replace('_', ' ')}</span></p>}
                      {offer.pay_rate != null && <p>Rate: <span className="text-foreground">${offer.pay_rate.toLocaleString()}</span></p>}
                      {offer.bonus_criteria && <p>Bonus: <span className="text-foreground">{offer.bonus_criteria}</span></p>}
                    </div>
                  </div>

                  {/* Hours */}
                  {workingDays.length > 0 && (
                    <div>
                      <div className="flex items-center gap-1.5 font-medium mb-1">
                        <Clock className="h-3.5 w-3.5" /> Working Hours
                      </div>
                      <div className="text-muted-foreground space-y-0.5 pl-5">
                        {workingDays.map((d: any) => (
                          <p key={d.day}>
                            <span className="text-foreground">{DAY_LABELS[d.day] || d.day}:</span> {to12Hour(d.start)} – {to12Hour(d.end)}
                          </p>
                        ))}
                        {(offer as any).lunch_break_start && (offer as any).lunch_break_end && (
                          <p>
                            <span className="text-foreground">Lunch:</span> {to12Hour((offer as any).lunch_break_start)} – {to12Hour((offer as any).lunch_break_end)}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Benefits */}
                  <div>
                    <div className="flex items-center gap-1.5 font-medium mb-1">
                      <Heart className="h-3.5 w-3.5" /> Benefits
                    </div>
                    <div className="flex flex-wrap gap-1.5 pl-5">
                      {offer.health_insurance && <Badge variant="outline" className="text-xs">Health Insurance</Badge>}
                      {offer.dental_insurance && <Badge variant="outline" className="text-xs">Dental</Badge>}
                      {offer.vision_insurance && <Badge variant="outline" className="text-xs">Vision</Badge>}
                      {offer.life_insurance && <Badge variant="outline" className="text-xs">Life Insurance</Badge>}
                      {offer.retirement_401k && <Badge variant="outline" className="text-xs">401(k){offer.retirement_match ? ` (${offer.retirement_match})` : ''}</Badge>}
                      {offer.pto_days != null && offer.pto_days > 0 && <Badge variant="outline" className="text-xs">{offer.pto_days} PTO Days</Badge>}
                      {offer.uniforms_provided && <Badge variant="outline" className="text-xs">Uniforms</Badge>}
                      {offer.tools_provided && <Badge variant="outline" className="text-xs">Tools Provided</Badge>}
                      {offer.additional_benefits && <Badge variant="outline" className="text-xs">{offer.additional_benefits}</Badge>}
                    </div>
                  </div>

                  {/* Notes */}
                  {offer.notes && (
                    <div>
                      <div className="flex items-center gap-1.5 font-medium mb-1">
                        <StickyNote className="h-3.5 w-3.5" /> Notes
                      </div>
                      <p className="text-muted-foreground pl-5 whitespace-pre-wrap">{offer.notes}</p>
                    </div>
                  )}

                  {/* Accept / Decline */}
                  {isPending && (
                    <div className="pt-2 border-t space-y-3">
                      {isDeclineFlow ? (
                        <div className="space-y-2">
                          <p className="text-sm font-medium">Reason for declining (optional):</p>
                          <Textarea
                            placeholder="Let the shop know why you're declining…"
                            value={declineReason}
                            onChange={e => setDeclineReason(e.target.value)}
                            rows={3}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={respond.isPending}
                              onClick={() => respond.mutate({ offerId: offer.id, action: 'declined', reason: declineReason || undefined })}
                            >
                              Confirm Decline
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => { setDecliningId(null); setDeclineReason(''); }}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            disabled={respond.isPending}
                            onClick={() => { setAcceptingId(offer.id); setStartDate(''); }}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Accept Offer
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={respond.isPending}
                            onClick={() => setDecliningId(offer.id)}
                          >
                            <XCircle className="h-3.5 w-3.5 mr-1" /> Decline
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>

      {/* Accept + Start Date Dialog */}
      <Dialog open={!!acceptingId} onOpenChange={(open) => { if (!open) { setAcceptingId(null); setStartDate(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept Offer</DialogTitle>
            <DialogDescription>When would you like to start?</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
            />
          </div>
          {rosterDisclosure.state === 'ready' && (
            <div role="note" data-testid="connect-roster-adoption-disclosure"
              className="space-y-1 rounded-md border border-amber-500 bg-amber-50 p-3 text-sm text-amber-950 dark:bg-amber-950 dark:text-amber-50">
              {describeRosterAdoption(rosterDisclosure.entry, acceptingOffer?.shops?.name ?? '', 'offer').map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          )}
          {rosterDisclosure.state === 'unavailable' && (
            <p role="status" data-testid="connect-roster-adoption-unavailable" className="text-sm text-destructive">
              This offer takes over a roster entry that can no longer be taken over. Ask the shop to update the offer.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAcceptingId(null); setStartDate(''); }}>Cancel</Button>
            <Button
              data-testid="connect-offer-confirm-accept"
              disabled={!startDate || respond.isPending || rosterBlocksAccept}
              onClick={() => {
                if (acceptingId) {
                  respond.mutate({ offerId: acceptingId, action: 'accepted', start_date: startDate, disclosure: rosterDisclosure });
                }
              }}
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              {rosterDisclosure.state === 'ready' ? 'Accept and take over this entry' : 'Confirm Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
